import React, { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useOutletContext } from "react-router-dom";

import CharacterShopCard from "@components/character-shop-card/character-shop-card";
import Coins from "@components/coins/coins";
import EmptyQueryHandler from "@components/empty-query-handler/empty-query-handler";
import ChevronSVG from "@components/icons/chevron-svg";

import { useAppSelector } from "@hooks/use-store";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { addDividerOnModal } from "@helpers/helpers";
import { CHARACTERS } from "@constants/characters.constant";

import { useGetCharactersQuery } from "../../api/characters";
import { useGetExerciseEntrypointQuery } from "../../api/exercise-entrypoint";

function Store() {
  const { characters, activeCharacterName } = useAppSelector(
    (state) => state.app,
  );

  const navigate = useNavigate();

  const modalBody = useRef<HTMLDivElement>(null);
  const hr = useRef<HTMLHRElement>(null);

  const { data } = useGetExerciseEntrypointQuery({
    exerciseId: 0,
  });
  const { isLoading } = useGetCharactersQuery();

  const { closeModal } = useOutletContext<{
    closeModal?: () => void;
  }>();

  const typeBtn = (item: any): string => {
    if (item.name === activeCharacterName) {
      if (data?.user.gameMode) {
        return "selected";
      }
      return "purchased";
    }
    if (item.purchased) return "purchased";
    if (item.currencyCost === 0) {
      if (data && data?.user.coins < item.cost) return "disabled";
    } else {
      return "currencyCost";
    }

    return "";
  };

  useEffect(() => {
    addDividerOnModal(modalBody, hr);
    const close = (event: any) => {
      if (event.keyCode === 27) {
        navigate(formatUrlWithLocale("/typing-tutor/settings/"));
      }
    };
    window.addEventListener("keyup", close);

    return () => window.removeEventListener("keyup", close);
  }, [navigate, isLoading]);

  return (
    <EmptyQueryHandler isLoading={isLoading}>
      <div className='modal-header p-4 pb-0 md:p-6 md:pb-0'>
        <div className='relative text-center'>
          <button
            type='button'
            onClick={() =>
              navigate(formatUrlWithLocale("/typing-tutor/settings/"))
            }
            className='absolute left-0 top-0'
          >
            <ChevronSVG
              width={30}
              height={30}
              classNameProps='fill-icon-secondary dark:fill-icon-dark-secondary'
            />
          </button>
          <h3>
            <FormattedMessage id='buy-hero' />
          </h3>
          {data ? (
            <Coins coins={data.user.coins} classNameProps='mb-4 pt-2 md:mb-5' />
          ) : null}
        </div>
      </div>
      <hr
        className='mx-4 mt-1 border-t-2 border-transparent transition-all md:mx-6'
        ref={hr}
      />
      <div className='modal-body p-4 pt-0 md:p-6 md:pt-0' ref={modalBody}>
        <div className='-mt-4 flex flex-wrap md:-mt-6'>
          {characters
            .filter((item) => item.active)
            .map((item) => {
              return (
                <CharacterShopCard
                  key={item.id}
                  id={String(item.id)}
                  typeBtn={typeBtn(item)}
                  name={item.name}
                  coins={item.cost}
                  paymentForm={item.paymentForm}
                >
                  <div className='mx-auto mb-1 max-w-[100px] overflow-hidden rounded-full bg-white'>
                    <img
                      src={CHARACTERS[item.name].gif}
                      alt={item.name}
                      className='p-1'
                    />
                  </div>
                </CharacterShopCard>
              );
            })}
        </div>
      </div>
    </EmptyQueryHandler>
  );
}

export default Store;
